import { MotionType } from 'consts/motionType';

import DataListProps from 'lib/DataList/DataList.types';
import { CodeNameModel, CreativeType } from './index';
import { Advertiser, AdvertiserWithBrands, Brand, BrandWithProductCategory, Dsp } from './Deal.types';
import { FrameTag } from './FrameTag.types';
import { FrameOrdering } from './FrameOrdering.types';

export type CreativeMarketDeal = {
  id: string;
  dealId: string;
  dealName?: string;
  inherited?: boolean;
};

export type CreativeMarketTag = {
  id: string;
  type: string;
  tag: FrameTag;
};

export interface CreativeMarket {
  agency: CodeNameModel | null;
  assignedTo: null;
  auditLogs: { id: string; createdAt: number }[];
  comments: [];
  commercial: {
    id: string;
    brand: {
      id: string;
      name: string;
    };
    advertiser: {
      id: string;
      environment: string;
      code: string;
      name: string;
    };
    productCategory?: CodeNameModel;
  };
  deal: CreativeMarketDeal[];
  deleted: boolean | null;
  deletedAt: string | null;
  environment: string;
  id: string;
  moderation: Moderation[];
  productCategory: CodeNameModel[];
  productFormat: CodeNameModel[];
  publisher: string;
  specialist: CodeNameModel | null;
  suggestedProductCategory: [];
}

export type CreativeId = string | number;

export enum CreativeErrorType {
  FAILURE = 'FAILURE',
  PARTIAL_SUCCESS = 'PARTIAL_SUCCESS',
}

export interface CreativeError {
  result: string;
  reason: string;
}

export interface UpdateCreativeResponseError extends CreativeError {
  id: string;
}

export interface Moderation {
  automatic?: boolean;
  comment: string;
  id: string;
  status: CreativeStatus;
  updateStamp: number;
  updatedBy: CodeNameModel;
}

export type AuditDetails = Moderation & {
  id: string;
  createdAt: string | Date;
  type: CreativeStatus;
  user: CodeNameModel;
  publisher?: string;
};

export interface FrameSummary {
  inclusionListCount: number;
  exclusionListCount: number;
}

export interface Creative {
  additionalProductCategories: CodeNameModel[];
  additionalProductCategoriesOptions: CodeNameModel[];
  advertiser: Advertiser | null;
  agency: CodeNameModel | null;
  allAdditionalProductCategoriesOptions: CodeNameModel[];
  brand: BrandWithProductCategory | null;
  commercialId: string;
  contentURL: string;
  convertStatus: ConvertStatus;
  dealId: CreativeMarketDeal[];
  deleted: boolean | null;
  deletedAt?: string | null;
  downloadURL: string;
  downloadS3URL: string;
  dsp: Dsp;
  duration: number;
  error?: CreativeError;
  expiredAt?: string;
  externalAdvertiserName: string;
  clickThroughUrl: string;
  externalAgencyId: string;
  externalId: string;
  frameSummary: FrameSummary;
  frameTags: FrameTag[];
  height: number;
  id: CreativeId;
  isExpired?: boolean;
  market: CreativeMarket[];
  marketId: string;
  moderation: Moderation[];
  movement: MotionType;
  previewURL: string;
  productCategory: CodeNameModel | null;
  productFormat: CodeNameModel[];
  specialist: CodeNameModel | null;
  statusCode: CreativeStatus;
  thumbnail: string;
  type: CreativeType;
  uploaded?: number;
  uploadedBy?: string;
  uploadedDate: string;
  uploadedTime: string;
  width: number;
}

export interface CreativesFilters extends AdvancedFilters {
  status: string[];
  automatic?: string;
  dsp?: CodeNameModel;
  dealIds?: CodeNameModel[] | [];
  advertiser?: CodeNameModel;
  brand?: CodeNameModel;
  productCategory?: CodeNameModel;
  deleted: boolean;
}

export interface AdvancedFilters {
  status: string[];
  startDate: Date | null;
  endDate: Date | null;
  creativeType: string;
  productFormat: CodeNameModel | null;
  externalId: string;
  externalAgency: string | undefined;
  externalAdvertiserName: string | undefined;
  clickThroughUrl: string | undefined;
  width: string;
  height: string;
  dealIds?: CodeNameModel[];
  advertiser?: CodeNameModel;
  brand?: CodeNameModel;
  productCategory?: CodeNameModel;
}

export interface CreativesMetaFormModel {
  dealId: DataListProps[];
  advertiser: AdvertiserWithBrands | null;
  brand: BrandWithProductCategory | null;
  productCategory: CodeNameModel | null;
  agency: CodeNameModel | null;
  specialist: CodeNameModel | null;
  productFormat: CodeNameModel[];
  movement: MotionType | null;
}

export interface CreativeMetaOperationsModel {
  dealId: CreativeMetaOperation;
  productFormat: CreativeMetaOperation;
}

export interface CreativeCountSummary {
  staticCreatives: number;
  motionCreatives: number;
}

export enum CreativeMetaOperation {
  ADD = 'ADD',
  REPLACE = 'REPLACE',
}

export class UpdateCreativesError extends Error {
  constructor(
    message: string,
    readonly errorList: UpdateCreativeResponseError[],
  ) {
    super(message);
    this.name = 'UpdateCreativesError';
  }
}

export interface CreativePanel {
  id: string;
  previewUrl: string;
  status: CreativeStatus;
  thumbnailUrl: string;
  type: CreativeType;
}

export enum ConvertStatus {
  REQUESTED = 'REQUESTED',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
  NOT_STARTED = 'NOT_STARTED',
}

export enum CreativeStatus {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  FLAGGED = 'FLAGGED',
  REJECTED = 'REJECTED',
  INACTIVE = 'INACTIVE',
  UPLOADED = 'UPLOADED',
  DELETED = 'DELETED',
  RESTORED = 'RESTORED',
  SENT_FOR_APPROVAL = 'SENT_FOR_APPROVAL',
}

export interface CreativeSetPanel {
  channels: string[];
  cities: string[];
  count: number;
  creative: CreativePanel;
  height: number;
  id: string;
  motionType: MotionType;
  width: number;
  order?: number;
  frameCodes?: string[];
}

export interface CreativeSetFilters {
  dealId: CodeNameModel | null;
  environment: string;
  visualUnitCode: string;
  frameCode: string;
  location: string;
  framesOrder: FrameOrdering;
}

export interface CreativeSetDetails {
  advertiser: Advertiser;
  brand: Brand;
  code: string;
  dsp: CodeNameModel;
  filters: CreativeSetFilters;
  id: string;
  isEditingDisabled: boolean;
  isNewSet: boolean;
  isPanelView: boolean;
  panels: CreativeSetPanel[];
  status: CreativeStatus;
  visualUnitIds: string[];
}

export type CreativesRequestData = {
  status?: string[];
  statusFilter?: string;
  type: string;
  dspName?: string;
  productFormatName?: string;
  externalIdContains: string;
  width: string;
  height: string;
  dealId?: string;
  advertiserCode?: string;
  brandCode?: string;
  productCategoryCode?: string;
  deleted: boolean;
  startUploadDate?: string;
  endUploadDate?: string;
  externalAdvertiserName?: string;
  clickThroughUrl?: string;
};

export type CreativesListItem = Creative & {
  uploadedBy?: CodeNameModel;
};

export type PreparedCreativesListItem = {
  advertiser?: string | null;
  brand?: string;
  deletedAt?: string | null;
  dspName?: string;
  environment?: string;
  expiredAt?: string;
  id: string;
  isExpired: boolean;
  productCategory?: string;
  externalAgencyId?: string;
  externalAdvertiserName?: string;
  clickThroughUrl?: string;
  statusCode: CreativeStatus;
  uploadedBy?: string;
  formatName: string;
  updatedBy?: string;
  updated?: number;
  agency?: string | null;
  specialist?: string | null;
};
